import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { StreakData } from '../types';
import { createStreakError } from './errorHandling';

/**
 * Saves streak data to Firestore user document
 * @param userId - User ID
 * @param streakData - Streak data to save
 * @returns Promise<boolean> - Success status
 */
export const saveStreakToFirestore = async (userId: string, streakData: StreakData): Promise<boolean> => {
  try {
    if (!userId) {
      throw createStreakError('No user ID provided for streak save');
    }

    const userRef = doc(db, 'users', userId);
    
    // Convert Date objects to ISO strings for Firestore
    const firestoreStreakData = {
      ...streakData,
      lastUpdated: streakData.lastUpdated.toISOString(),
    };

    await updateDoc(userRef, {
      'gamification.streak': firestoreStreakData
    });

    console.log('✅ Streak data saved to Firestore:', firestoreStreakData);
    return true;
  } catch (error) {
    console.error('❌ Failed to save streak to Firestore:', error);
    return false;
  }
};

/**
 * Loads streak data from Firestore user document
 * @param userId - User ID
 * @returns Promise<StreakData | null> - Streak data or null if not found/error
 */
export const loadStreakFromFirestore = async (userId: string): Promise<StreakData | null> => {
  try {
    if (!userId) {
      throw createStreakError('No user ID provided for streak load');
    }

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      console.log('📄 User document does not exist');
      return null;
    }

    const userData = userDoc.data();
    const firestoreStreakData = userData?.gamification?.streak;

    if (!firestoreStreakData) {
      console.log('📊 No streak data found in Firestore');
      return null;
    }

    // Convert ISO string back to Date object and validate data
    const streakData: StreakData = {
      current: typeof firestoreStreakData.current === 'number' ? firestoreStreakData.current : 0,
      longest: typeof firestoreStreakData.longest === 'number' ? firestoreStreakData.longest : 0,
      lastUpdated: firestoreStreakData.lastUpdated ? new Date(firestoreStreakData.lastUpdated) : new Date(),
      lastActivityDate: typeof firestoreStreakData.lastActivityDate === 'string' ? firestoreStreakData.lastActivityDate : '',
      milestones: Array.isArray(firestoreStreakData.milestones) ? firestoreStreakData.milestones : [3, 7, 30, 100],
      milestonesReached: Array.isArray(firestoreStreakData.milestonesReached) ? firestoreStreakData.milestonesReached : [],
      celebrationShownToday: typeof firestoreStreakData.celebrationShownToday === 'boolean' ? firestoreStreakData.celebrationShownToday : false,
      streakMissedAcknowledged: typeof firestoreStreakData.streakMissedAcknowledged === 'boolean' ? firestoreStreakData.streakMissedAcknowledged : false,
      lastBrokenStreak: typeof firestoreStreakData.lastBrokenStreak === 'number' ? firestoreStreakData.lastBrokenStreak : 0,
    };

    console.log('✅ Streak data loaded from Firestore:', streakData);
    return streakData;
  } catch (error) {
    console.error('❌ Failed to load streak from Firestore:', error);
    return null;
  }
};

/**
 * Initializes streak data in Firestore if it doesn't exist
 * @param userId - User ID
 * @param initialStreakData - Initial streak data
 * @returns Promise<boolean> - Success status
 */
export const initializeStreakInFirestore = async (userId: string, initialStreakData: StreakData): Promise<boolean> => {
  try {
    if (!userId) {
      throw createStreakError('No user ID provided for streak initialization');
    }

    // Check if streak data already exists
    const existingStreak = await loadStreakFromFirestore(userId);
    if (existingStreak) {
      console.log('📊 Streak data already exists in Firestore');
      return true;
    }

    // Initialize with provided data
    const success = await saveStreakToFirestore(userId, initialStreakData);
    if (success) {
      console.log('🎯 Streak data initialized in Firestore');
    }
    return success;
  } catch (error) {
    console.error('❌ Failed to initialize streak in Firestore:', error);
    return false;
  }
};

/**
 * Syncs local streak data with Firestore
 * Loads from Firestore first, falls back to local storage, then initializes if needed
 * @param userId - User ID
 * @param localStreakData - Local streak data as fallback
 * @returns Promise<StreakData> - Synced streak data
 */
export const syncStreakData = async (userId: string, localStreakData: StreakData): Promise<StreakData> => {
  try {
    if (!userId) {
      console.log('🔄 No user ID, using local streak data');
      return localStreakData;
    }

    // Try to load from Firestore first
    const firestoreStreak = await loadStreakFromFirestore(userId);
    if (firestoreStreak) {
      console.log('🔄 Using Firestore streak data');
      return firestoreStreak;
    }

    // If no Firestore data, initialize with local data
    console.log('🔄 No Firestore data, initializing with local data');
    const success = await saveStreakToFirestore(userId, localStreakData);
    if (success) {
      console.log('✅ Local streak data synced to Firestore');
    }
    return localStreakData;
  } catch (error) {
    console.error('❌ Failed to sync streak data:', error);
    return localStreakData;
  }
};